import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  useMediaQuery,
  IconButton,
  Collapse,
  Switch,
  useTheme,
} from "@mui/material";
import { getCustomers, deleteCustomer, updateCustomer, setOutstandingLimit, getOutstandingBalance, toggleCustomerActive } from "../api/customerApi";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SetPricingModal from "../components/SetPricingModal";
import { toggleSubaccountCreation } from "../api/customerApi";
import { useNavigate } from "react-router-dom";

interface Customer {
  master_customer_id: number | null;
  allow_subaccount_creation: boolean;
  id: number;
  name: string;
  email: string;
  phone: string;
  shipping_address: string;
  subaccounts?: Customer[];
  isExpanded?: boolean;
  password?: string;
  outstanding_limit?: number;
  outstanding_balance?: number;
  active: boolean;
}

const CustomerListPage: React.FC = () => {
  const [groupedCustomers, setGroupedCustomers] = useState<Customer[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [currentOutstanding, setCurrentOutstanding] = useState<number>(0);
  const [newLimit, setNewLimit] = useState<number>(0);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const navigate = useNavigate();

  const openLimitDialog = async (customerId: number, currentLimit: number | undefined) => {
    try {
      const data = await getOutstandingBalance(customerId);
      setCurrentOutstanding(data.outstandingBalance);
      setNewLimit(data.outstandingLimit || 0);
      setSelectedCustomerId(customerId);
      setLimitDialogOpen(true);
    } catch (error) {
      console.error("Error fetching outstanding balance:", error);
    }
  };

  useEffect(() => {
    const loadCustomers = async () => {
      try {
        const data = await getCustomers();
        const customersWithBalances = await Promise.all(
          data.map(async (customer: Customer) => {
            try {
              const balanceData = await getOutstandingBalance(customer.id);
              return {
                ...customer,
                outstanding_balance: balanceData.outstandingBalance,
                outstanding_limit: balanceData.outstandingLimit,
              };
            } catch (error) {
              console.error("Error fetching balance for customer", customer.id, error);
              return customer;
            }
          })
        );
        const grouped = groupCustomers(customersWithBalances);
        setGroupedCustomers(grouped);
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    };
    loadCustomers();
  }, []);

  const groupCustomers = (customers: Customer[]): Customer[] => {
    const masters = customers.filter(c => c.master_customer_id === null);
    return masters.map(master => ({
      ...master,
      subaccounts: customers.filter(c => c.master_customer_id === master.id),
      isExpanded: false
    }));
  };

  const toggleExpand = (masterId: number) => {
    setGroupedCustomers(prev => prev.map(master => 
      master.id === masterId ? { ...master, isExpanded: !master.isExpanded } : master
    ));
  };

  const handleEdit = (customer: Customer) => {
    setEditCustomer({
      ...customer,
      password: ""
    });
    setEditDialogOpen(true);
  };

  const handleUpdateCustomer = async () => {
    if (editCustomer) {
      try {
        await updateCustomer(editCustomer.id, editCustomer);
        setGroupedCustomers(prev => 
          prev.map(master => ({
            ...master,
            subaccounts: master.subaccounts?.map(sub => 
              sub.id === editCustomer.id ? editCustomer : sub
            ),
            ...(master.id === editCustomer.id ? editCustomer : {})
          }))
        );
        setEditDialogOpen(false);
      } catch (error) {
        console.error("Error updating customer:", error);
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteCustomer(id.toString());
      setGroupedCustomers(prev => 
        prev.filter(master => master.id !== id)
          .map(master => ({
            ...master,
            subaccounts: master.subaccounts?.filter(sub => sub.id !== id)
          }))
      );
    } catch (error) {
      console.error("Error deleting customer:", error);
    }
  };

  const handleSetPricing = (customer: Customer) => {
    setSelectedCustomer(customer);
    setPricingModalOpen(true);
  };

  const MobileCustomerRow = ({ customer, isSubaccount }: { customer: Customer, isSubaccount?: boolean }) => (
    <Box
      sx={{
        marginBottom: 1,
        padding: 2,
        border: "1px solid #ddd",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        ml: isSubaccount ? 4 : 0,
        backgroundColor: isSubaccount ? 'action.hover' : 'background.paper'
      }}
    >
      <Typography variant="subtitle1" sx={{textDecoration: "underline", cursor:"pointer", color:"primary.main"}}
        onClick={() => navigate(`/dashboard/customers/${customer.id}/overview`)}
        >{customer.name}</Typography>
      <Typography variant="body2">Email: {customer.email}</Typography>
      <Typography variant="body2">Phone: {customer.phone}</Typography>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            Credit Limit: {customer.outstanding_limit !== undefined ? `$${customer.outstanding_limit}` : "N/A"}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => openLimitDialog(customer.id, customer.outstanding_limit)}
            startIcon={<EditIcon fontSize="small" />}
          >
            Edit
          </Button>
        </Box>
        
        <Typography variant="body2">
          Outstanding Balance: {customer.outstanding_balance !== undefined ? `$${customer.outstanding_balance.toFixed(2)}` : "N/A"}
        </Typography>
      </Box>

      {customer.master_customer_id === null && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={customer.allow_subaccount_creation || false}
              onChange={async (e) => {
                const newValue = e.target.checked;
                try {
                  await toggleSubaccountCreation(customer.id, newValue);
                  setGroupedCustomers(prev =>
                    prev.map(m =>
                      m.id === customer.id ? { ...m, allow_subaccount_creation: newValue } : m
                    )
                  );
                } catch (error) {
                  console.error("Failed to toggle subaccount creation:", error);
                }
              }}
            />
            <Typography variant="body2">
              Allow Subaccount Creation
            </Typography>
          </Box>
          <Typography variant="body2">
            Subaccounts: {customer.subaccounts?.length || 0}
          </Typography>
        </>
      )}
      
      <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch
            checked={customer.active}
            onChange={async (e) => {
              const newValue = e.target.checked;
              try {
                await toggleCustomerActive(customer.id, newValue);
                setGroupedCustomers(prev =>
                  prev.map(c =>
                    c.id === customer.id ? { ...c, active: newValue } : c
                  )
                );
              } catch (error) {
                console.error("Failed to toggle customer status:", error);
              }
            }}
          />
          <Typography variant="body2">
            {customer.active ? 'Active' : 'Disabled'}
          </Typography>
        </Box>
        
        <Button
          variant="outlined"
          onClick={() => handleEdit(customer)}
          startIcon={<EditIcon />}
          size="small"
        >
          Edit
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => handleDelete(customer.id)}
          startIcon={<DeleteIcon />}
          size="small"
        >
          Delete
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleSetPricing(customer)}
          size="small"
        >
          Pricing
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ marginBottom: 2 }}>
        Manage Customers
      </Typography>
      <Paper sx={{ overflowX: 'auto' }}>
        {isMobile ? (
          groupedCustomers.map((master) => (
            <Box key={master.id} sx={{ mb: 2 }}>
              <MobileCustomerRow customer={master} />
              <IconButton
                size="small"
                onClick={() => toggleExpand(master.id)}
                sx={{ ml: 2 }}
              >
                {master.isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                <Typography variant="caption">
                  {master.isExpanded ? 'Hide' : 'Show'} Subaccounts ({master.subaccounts?.length || 0})
                </Typography>
              </IconButton>
              <Collapse in={master.isExpanded}>
                {master.subaccounts?.map((subaccount) => (
                  <MobileCustomerRow key={subaccount.id} customer={subaccount} isSubaccount />
                ))}
              </Collapse>
            </Box>
          ))
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Shipping Address</TableCell>
                <TableCell>Subaccounts</TableCell>
                <TableCell>Allow Subaccounts</TableCell>
                <TableCell>Credit Limit</TableCell>
                <TableCell>Outstanding Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedCustomers.map((master) => (
                <React.Fragment key={master.id}>
                  <TableRow sx={{ '& > td': { borderBottom: 'unset' } }}>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => toggleExpand(master.id)}
                      >
                        {master.isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                      onClick={() => navigate(`/dashboard/customers/${master.id}/overview`)}
                    >
                      {master.name}
                    </TableCell>
                    <TableCell>{master.email}</TableCell>
                    <TableCell>{master.phone}</TableCell>
                    <TableCell>{master.shipping_address}</TableCell>
                    <TableCell>{master.subaccounts?.length || 0}</TableCell>
                    <TableCell>
                      <Switch
                        checked={master.allow_subaccount_creation || false}
                        onChange={async (e) => {
                          const newValue = e.target.checked;
                          try {
                            await toggleSubaccountCreation(master.id, newValue);
                            setGroupedCustomers(prev =>
                              prev.map(m =>
                                m.id === master.id ? { ...m, allow_subaccount_creation: newValue } : m
                              )
                            );
                          } catch (error) {
                            console.error("Failed to toggle subaccount creation:", error);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography>
                          {master.outstanding_limit !== undefined ? `$${master.outstanding_limit}` : "N/A"}
                        </Typography>
                        <IconButton
                          onClick={() => openLimitDialog(master.id, master.outstanding_limit)}
                          size="small"
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {master.outstanding_balance !== undefined ? `$${master.outstanding_balance.toFixed(2)}` : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={master.active}
                        onChange={async (e) => {
                          const newValue = e.target.checked;
                          try {
                            await toggleCustomerActive(master.id, newValue);
                            setGroupedCustomers(prev =>
                              prev.map(m =>
                                m.id === master.id ? { ...m, active: newValue } : m
                              )
                            );
                          } catch (error) {
                            console.error("Failed to toggle customer status:", error);
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => handleEdit(master)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(master.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  
                  <TableRow>
                    <TableCell colSpan={11} sx={{ py: 0 }}>
                      <Collapse in={master.isExpanded}>
                        <Box sx={{ margin: 1 }}>
                          <Table size="small">
                            <TableBody>
                              {master.subaccounts?.map((subaccount) => (
                                <TableRow key={subaccount.id} sx={{ backgroundColor: 'action.hover' }}>
                                  <TableCell />
                                  <TableCell>{subaccount.name}</TableCell>
                                  <TableCell>{subaccount.email}</TableCell>
                                  <TableCell>{subaccount.phone}</TableCell>
                                  <TableCell>{subaccount.shipping_address}</TableCell>
                                  <TableCell>Subaccount</TableCell>
                                  <TableCell>N/A</TableCell>
                                  <TableCell>N/A</TableCell>
                                  <TableCell>
                                    {subaccount.outstanding_balance !== undefined ? `$${subaccount.outstanding_balance.toFixed(2)}` : "N/A"}
                                  </TableCell>
                                  <TableCell>
                                    <Switch
                                      checked={subaccount.active}
                                      onChange={async (e) => {
                                        const newValue = e.target.checked;
                                        try {
                                          await toggleCustomerActive(subaccount.id, newValue);
                                          setGroupedCustomers(prev =>
                                            prev.map(m =>
                                              m.id === master.id ? {
                                                ...m,
                                                subaccounts: m.subaccounts?.map(s =>
                                                  s.id === subaccount.id ? { ...s, active: newValue } : s
                                                )
                                              } : m
                                            )
                                          );
                                        } catch (error) {
                                          console.error("Failed to toggle subaccount status:", error);
                                        }
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <IconButton onClick={() => handleEdit(subaccount)} color="primary">
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(subaccount.id)} color="error">
                                      <DeleteIcon />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Edit Customer Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Customer</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={editCustomer?.name || ""}
            onChange={(e) =>
              setEditCustomer((prev) =>
                prev ? { ...prev, name: e.target.value } : prev
              )
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            value={editCustomer?.email || ""}
            onChange={(e) =>
              setEditCustomer((prev) =>
                prev ? { ...prev, email: e.target.value } : prev
              )
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Phone"
            value={editCustomer?.phone || ""}
            onChange={(e) =>
              setEditCustomer((prev) =>
                prev ? { ...prev, phone: e.target.value } : prev
              )
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Shipping Address"
            value={editCustomer?.shipping_address || ""}
            onChange={(e) =>
              setEditCustomer((prev) =>
                prev ? { ...prev, shipping_address: e.target.value } : prev
              )
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="New Password"
            type="password"
            value={editCustomer?.password || ""}
            onChange={(e) =>
              setEditCustomer((prev) =>
                prev ? { ...prev, password: e.target.value } : prev
              )
            }
            fullWidth
            margin="normal"
            placeholder="Leave blank to keep current password"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateCustomer}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credit Limit Dialog */}
      <Dialog 
        open={limitDialogOpen} 
        onClose={() => setLimitDialogOpen(false)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set Credit Limit</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              Current Outstanding Balance: ${currentOutstanding.toFixed(2)}
            </Typography>
            <Typography variant="body1">
              Current Credit Limit: {newLimit !== null ? `$${newLimit}` : "Not set"}
            </Typography>
          </Box>

          <TextField
            label="New Credit Limit"
            type="number"
            fullWidth
            variant="outlined"
            value={newLimit}
            onChange={(e) => setNewLimit(Number(e.target.value))}
            InputProps={{
              startAdornment: <Typography variant="body1" sx={{ mr: 1 }}>$</Typography>,
            }}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLimitDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              if (selectedCustomerId !== null) {
                try {
                  await setOutstandingLimit(selectedCustomerId, newLimit);
                  const data = await getCustomers();
                  const grouped = groupCustomers(data);
                  setGroupedCustomers(grouped);
                  setLimitDialogOpen(false);
                } catch (error) {
                  console.error("Failed to update credit limit:", error);
                }
              }
            }}
          >
            Update Limit
          </Button>
        </DialogActions>
      </Dialog>

      {selectedCustomer && (
        <SetPricingModal
          open={pricingModalOpen}
          customerId={selectedCustomer.id}
          customerName={selectedCustomer.name}
          onClose={() => {
            setPricingModalOpen(false);
            setSelectedCustomer(null);
          }}
        />
      )}
    </Box>
  );
};

export default CustomerListPage;